import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../services/auth.service';

type LoginStep = 'credentials' | 'twoFactor';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent {
  username = '';
  password = '';
  twoFactorCode = '';
  rememberMe = true;
  showPassword = false;
  isSubmitting = false;
  errorMessage = '';
  loginStep: LoginStep = 'credentials';
  pendingEmail = '';
  qrCodeBase64 = '';

  constructor(
    private authService: AuthService,
    private router: Router
  ) { }

  get canSubmit(): boolean {
    if (this.loginStep === 'twoFactor') {
      return this.twoFactorCode.trim().length > 0 && !this.isSubmitting;
    }

    return this.username.trim().length > 0 && this.password.length > 0 && !this.isSubmitting;
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  login() {
    if (this.loginStep === 'twoFactor') {
      this.verifyTwoFactor();
      return;
    }

    this.errorMessage = '';

    if (!this.username.trim() || !this.password) {
      this.errorMessage = 'Please enter your username and password.';
      return;
    }

    this.isSubmitting = true;

    this.authService.login(this.username.trim(), this.password)
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe({
        next: (res) => {
          if (this.isFailedLogin(res)) {
            this.errorMessage = 'Login failed. Please check your username and password.';
            return;
          }

          this.pendingEmail = this.extractEmail(res) || this.username.trim();
          this.qrCodeBase64 = this.extractQrCode(res);
          this.loginStep = 'twoFactor';
          this.password = '';
          this.errorMessage = '';
        },
        error: (err) => {
          this.errorMessage = this.extractErrorMessage(err, 'Login failed. Please check your username and password.');
        }
      });
  }

  verifyTwoFactor() {
    this.errorMessage = '';

    if (!this.pendingEmail && !this.username.trim()) {
      this.loginStep = 'credentials';
      this.errorMessage = 'Please login again before verifying your code.';
      return;
    }

    if (!this.twoFactorCode.trim()) {
      this.errorMessage = 'Please enter your verification code.';
      return;
    }

    this.isSubmitting = true;

    this.authService.verifyTwoFactor(this.pendingEmail || this.username.trim(), this.twoFactorCode.trim())
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe({
        next: (res) => {
          const token = this.extractToken(res) || this.extractHash(res);

          if (!token) {
            this.errorMessage = 'Verification completed, but no token was returned.';
            return;
          }

          this.completeLogin(token, res);
        },
        error: (err) => {
          this.errorMessage = this.extractErrorMessage(err, 'Verification failed. Please check your code.');
        }
      });
  }

  backToLogin() {
    this.loginStep = 'credentials';
    this.twoFactorCode = '';
    this.qrCodeBase64 = '';
    this.errorMessage = '';
  }

  private completeLogin(token: string, response: any) {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('adminUser', this.pendingEmail || this.username.trim());
    sessionStorage.setItem('authResponse', JSON.stringify(response));

    if (this.rememberMe) {
      sessionStorage.setItem('rememberAdmin', 'true');
    } else {
      sessionStorage.removeItem('rememberAdmin');
    }

    this.router.navigate(['/dashboard']);
  }

  private extractToken(response: any): string {
    return response?.token
      || response?.accessToken
      || response?.jwt
      || response?.data?.token
      || response?.data?.accessToken
      || response?.data?.jwt
      || '';
  }

  get qrCodeImageSrc(): string {
    if (!this.qrCodeBase64) {
      return '';
    }

    return this.qrCodeBase64.startsWith('data:image')
      ? this.qrCodeBase64
      : `data:image/png;base64,${this.qrCodeBase64}`;
  }

  private isFailedLogin(response: any): boolean {
    return response?.success === false
      || response?.Success === false
      || response?.data?.success === false
      || response?.data?.Success === false;
  }

  private extractQrCode(response: any): string {
    return response?.qrCodeBase64
      || response?.QrCodeBase64
      || response?.data?.qrCodeBase64
      || response?.data?.QrCodeBase64
      || '';
  }

  private extractHash(response: any): string {
    return response?.hash || response?.data?.hash || '';
  }

  private extractEmail(response: any): string {
    return response?.email
      || response?.username
      || response?.data?.email
      || response?.data?.username
      || '';
  }

  private extractErrorMessage(error: any, fallback: string): string {
    return error?.error?.message
      || error?.error?.title
      || error?.message
      || fallback;
  }
}
